import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bell, Check, Clock, User, Phone, Calendar, XCircle, Volume2, Trash2, Edit2, AlertCircle, UserX, HelpCircle } from 'lucide-react';
import DataManager from '../components/DataManager';
import { playNotificationSound, requestNotificationPermission, showNotification } from '../utils/notifications';
import { safeFormat } from '../utils/dateUtils';

const QueuePage = () => {
  const { t } = useTranslation();
  const { isAdmin, isBarber, user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const previousQueueLength = useRef(0);
  const previousNotificationsLength = useRef(0);
  
  // للإجراءات
  const [actionModal, setActionModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [actionStatus, setActionStatus] = useState('completed');
  const [actionNote, setActionNote] = useState('');

  useEffect(() => {
    // Request notification permission on mount
    requestNotificationPermission();
    fetchData();
    // Poll for new notifications every 15 seconds (faster for better UX)
    const interval = setInterval(checkForNewBookings, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for new bookings and play sound
  const checkForNewBookings = async () => {
    try {
      const [queueRes, notificationsRes] = await Promise.all([
        api.get('/queue'),
        api.get('/notifications')
      ]);
      
      const newQueue = queueRes.data;
      const newNotifications = notificationsRes.data;
      
      // Check if there are new bookings
      const hasNewBooking = newQueue.length > previousQueueLength.current;
      const hasNewNotification = newNotifications.filter(n => !n.read).length > 
                                  notifications.filter(n => !n.read).length;
      
      if ((hasNewBooking || hasNewNotification) && soundEnabled) {
        // Play sound for new booking
        playNotificationSound();
        
        // Show toast notification
        const newBookings = newQueue.filter(q => 
          !queue.find(old => old.id === q.id)
        );
        
        if (newBookings.length > 0) {
          const booking = newBookings[0];
          toast.success(
            `🔔 حجز جديد! ${booking.full_name} - ${booking.booking_time}`,
            { duration: 10000 }
          );
          
          // Show browser notification
          showNotification(
            '🔔 حجز جديد!',
            `${booking.full_name} حجز موعد الساعة ${booking.booking_time}`
          );
        }
      }
      
      previousQueueLength.current = newQueue.length;
      previousNotificationsLength.current = newNotifications.filter(n => !n.read).length;
      
      setQueue(newQueue);
      setNotifications(newNotifications);
    } catch (error) {
      console.error('Failed to check for new bookings');
    }
  };

  const fetchData = async () => {
    try {
      const [queueRes, notificationsRes, barbersRes] = await Promise.all([
        api.get('/queue'),
        api.get('/notifications'),
        api.get('/barbers')
      ]);
      setQueue(queueRes.data);
      setNotifications(notificationsRes.data);
      setBarbers(barbersRes.data);
      previousQueueLength.current = queueRes.data.length;
      previousNotificationsLength.current = notificationsRes.data.filter(n => !n.read).length;
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  // Test notification sound
  const testSound = () => {
    playNotificationSound();
    toast.info('🔊 اختبار صوت الإشعارات');
  };

  const handleStatusUpdate = async (queueId, status) => {
    try {
      await api.put(`/queue/${queueId}/status?status=${status}`);
      toast.success('تم تحديث الحالة');
      fetchData();
    } catch (error) {
      toast.error('فشل تحديث الحالة');
    }
  };

  // فتح نافذة الإجراء
  const openActionModal = (entry) => {
    setSelectedEntry(entry);
    setActionStatus('completed');
    setActionNote('');
    setActionModal(true);
  };

  // تنفيذ الإجراء
  const handleAction = async () => {
    if (!selectedEntry) return;
    try {
      await api.put(`/queue/${selectedEntry.id}/action`, {
        status: actionStatus,
        action_note: actionNote
      });
      const statusLabels = {
        completed: 'تم تنفيذ الطلب',
        late: 'تأخر العميل',
        no_show: 'لم يحضر العميل',
        cancelled_by_customer: 'تم الإلغاء من قبل العميل',
        cancelled: 'تم الإلغاء'
      };
      toast.success(`تم تسجيل: ${statusLabels[actionStatus] || actionStatus}`);
      setActionModal(false);
      setSelectedEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث الحالة');
    }
  };

  // حذف عنصر (للمدير فقط)
  const handleDelete = async (queueId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العنصر؟')) return;
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success('تم حذف العنصر');
      fetchData();
    } catch (error) {
      toast.error('فشل الحذف');
    }
  };

  // مسح جميع القائمة (للمدير فقط)
  const handleClearAll = async () => {
    if (!window.confirm('هل أنت متأكد من مسح جميع قائمة الانتظار؟')) return;
    try {
      const res = await api.delete('/queue/clear-all');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('فشل مسح القائمة');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const getBarberName = (barberId) => {
    const barber = barbers.find(b => b.id === barberId);
    return barber?.name || 'غير محدد';
  };

  const getStatusBadge = (status) => {
    const styles = {
      waiting: 'bg-yellow-500/20 text-yellow-500',
      in_service: 'bg-blue-500/20 text-blue-500',
      completed: 'bg-green-500/20 text-green-500',
      cancelled: 'bg-red-500/20 text-red-500',
      cancelled_by_customer: 'bg-orange-500/20 text-orange-500',
      late: 'bg-orange-500/20 text-orange-500',
      no_show: 'bg-red-500/20 text-red-500',
      other: 'bg-gray-500/20 text-gray-500'
    };
    const labels = {
      waiting: 'في الانتظار',
      in_service: 'قيد الخدمة',
      completed: 'تم التنفيذ',
      cancelled: 'ملغي',
      cancelled_by_customer: 'إلغاء من العميل',
      late: 'تأخر العميل',
      no_show: 'لم يحضر',
      other: 'أخرى'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.other}`}>
        {labels[status] || status}
      </span>
    );
  };

  // حساب الوقت المتبقي حتى الحذف التلقائي (أسبوع)
  const getTimeUntilDeletion = (createdAt) => {
    if (!createdAt) return null;
    try {
      const created = new Date(createdAt);
      const deleteDate = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000); // +7 أيام
      const now = new Date();
      const diff = deleteDate - now;
      
      if (diff <= 0) return { expired: true, text: 'سيُحذف قريباً' };
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) {
        return { expired: false, text: `${days} يوم ${hours} ساعة`, days, hours };
      }
      return { expired: false, text: `${hours} ساعة`, days: 0, hours };
    } catch {
      return null;
    }
  };

  if (!isAdmin() && !isBarber()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const unreadNotifications = notifications.filter(n => !n.read);
  const waitingQueue = queue.filter(q => q.status === 'waiting');
  const inServiceQueue = queue.filter(q => q.status === 'in_service');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">قائمة الانتظار</h1>
          <p className="text-muted-foreground">إدارة طلبات العملاء</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sound Toggle Button */}
          <Button
            data-testid="toggle-sound-btn"
            onClick={() => setSoundEnabled(!soundEnabled)}
            variant="outline"
            size="sm"
            className={`gap-2 ${soundEnabled ? 'border-green-500/50 text-green-500' : 'border-red-500/50 text-red-500'}`}
          >
            <Volume2 className="w-4 h-4" />
            {soundEnabled ? 'الصوت مفعّل' : 'الصوت مغلق'}
          </Button>
          
          {/* Test Sound Button */}
          <Button
            data-testid="test-sound-btn"
            onClick={testSound}
            variant="ghost"
            size="sm"
            className="gap-2"
          >
            🔊 اختبار
          </Button>
          
          {/* زر مسح جميع القائمة للمدير */}
          {isAdmin() && queue.length > 0 && (
            <Button
              onClick={handleClearAll}
              variant="outline"
              size="sm"
              className="gap-2 border-red-500/50 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4" />
              مسح الكل
            </Button>
          )}
          
          <DataManager
            collectionName="queue"
            collectionLabel="قائمة الانتظار"
            items={queue}
            onRefresh={fetchData}
            idField="id"
            nameField="customer_name"
            isAdmin={isAdmin()}
          />
        </div>
      </div>

      {/* Notifications Section */}
      {unreadNotifications.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-primary animate-pulse" />
            <h3 className="font-semibold text-primary">إشعارات جديدة ({unreadNotifications.length})</h3>
          </div>
          <div className="space-y-3">
            {unreadNotifications.map(notification => (
              <div key={notification.id} className="bg-card border border-white/5 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{notification.customer_name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-3 h-3" /> {notification.customer_phone}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-3 h-3" /> {notification.datetime}
                  </p>
                </div>
                <Button
                  onClick={() => markAsRead(notification.id)}
                  size="sm"
                  className="bg-primary text-primary-foreground"
                >
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">في الانتظار</p>
          <p className="text-3xl font-bold text-yellow-500">{waitingQueue.length}</p>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">قيد الخدمة</p>
          <p className="text-3xl font-bold text-blue-500">{inServiceQueue.length}</p>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">إجمالي اليوم</p>
          <p className="text-3xl font-bold text-primary">{queue.length}</p>
        </div>
      </div>

      {/* Queue Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        {/* معلومات الحذف التلقائي */}
        <div className="px-6 py-3 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-amber-500">السجلات تُحذف تلقائياً بعد أسبوع من الإنشاء</span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">العميل</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">رقم الهاتف</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الموعد</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحلاق</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحالة</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">يُحذف خلال</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">
                    لا توجد طلبات حالياً
                  </td>
                </tr>
              ) : (
                queue.map((item) => {
                  const deletion = getTimeUntilDeletion(item.created_at);
                  return (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {item.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground" dir="ltr">{item.phone}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {safeFormat(item.datetime, 'yyyy-MM-dd HH:mm', { fallback: '-' })}
                      </div>
                    </td>
                    <td className="px-6 py-4">{getBarberName(item.barber_id)}</td>
                    <td className="px-6 py-4">
                      {getStatusBadge(item.status)}
                      {item.action_note && (
                        <p className="text-xs text-muted-foreground mt-1">{item.action_note}</p>
                      )}
                      {item.action_by_name && (
                        <p className="text-xs text-muted-foreground mt-1">بواسطة: {item.action_by_name}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {deletion && (
                        <div className={`flex items-center gap-1 text-xs ${deletion.expired || deletion.days <= 1 ? 'text-red-500' : deletion.days <= 3 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                          <Clock className="w-3 h-3" />
                          {deletion.text}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* زر اتخاذ إجراء - للحلاق المختص والمدير */}
                        {(item.status === 'waiting' || item.status === 'in_service') && (isAdmin() || isBarber()) && (
                          <Button
                            onClick={() => openActionModal(item)}
                            size="sm"
                            className="bg-primary hover:bg-primary/80 text-primary-foreground"
                          >
                            اتخاذ إجراء
                          </Button>
                        )}
                        
                        {/* زر الحذف - للمدير فقط */}
                        {isAdmin() && (
                          <Button
                            onClick={() => handleDelete(item.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )})
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* نافذة اتخاذ الإجراء */}
      <Dialog open={actionModal} onOpenChange={setActionModal}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>اتخاذ إجراء - {selectedEntry?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">الإجراء</label>
              <Select value={actionStatus} onValueChange={setActionStatus}>
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="completed">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      تم تنفيذ الطلب
                    </div>
                  </SelectItem>
                  <SelectItem value="no_show">
                    <div className="flex items-center gap-2">
                      <UserX className="w-4 h-4 text-red-500" />
                      لم يحضر العميل
                    </div>
                  </SelectItem>
                  <SelectItem value="cancelled_by_customer">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-orange-500" />
                      إلغاء من قبل العميل
                    </div>
                  </SelectItem>
                  <SelectItem value="late">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      تأخر العميل
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">ملاحظات (اختياري)</label>
              <Input
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder="أضف ملاحظاتك هنا..."
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <Button onClick={handleAction} className="w-full bg-primary text-primary-foreground">
              تأكيد الإجراء
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QueuePage;
